/** Справочник кодов валютных операций (код + наименование). */

export type CurrencyOperationCodeItem = {
  code: string;
  title: string;
};

export const CURRENCY_OPERATION_CODES: CurrencyOperationCodeItem[] = [
  { code: "10100", title: "Авансовый платеж – экспорт товаров" },
  { code: "10200", title: "Платеж по факту – экспорт товаров" },
  { code: "11100", title: "Авансовый платеж – импорт товаров" },
  { code: "11200", title: "Платеж по факту – импорт товаров" },
  { code: "20100", title: "Авансовый платеж – экспорт услуг" },
  { code: "20200", title: "Платеж по факту – экспорт услуг" },
  { code: "20300", title: "Аренда у резидента" },
  { code: "21100", title: "Авансовый платеж – импорт услуг" },
  { code: "21200", title: "Платеж по факту – импорт услуг" },
  { code: "21300", title: "Аренда у нерезидента" },
  { code: "99010", title: "Возврат ошибочно зачисленных средств" },
];

const UVED_CODES_BY_DIRECTION: Record<"EXP" | "IMP", readonly string[]> = {
  /** Продажа ЦВ / экспорт */
  EXP: ["10100", "10200", "20100", "20200", "20300"],
  /** Покупка ЦВ / импорт */
  IMP: ["11100", "11200", "21100", "21200", "21300"],
};

/**
 * Список кодов для УВЭД по направлению контракта и признаку возвратной сделки.
 */
export const getCurrencyCodesForUved = (
  direction: "EXP" | "IMP",
  isReturnDeal: boolean
): CurrencyOperationCodeItem[] => {
  if (isReturnDeal) {
    return CURRENCY_OPERATION_CODES.filter((item) => item.code === "99010");
  }
  const allowed = new Set(UVED_CODES_BY_DIRECTION[direction]);
  return CURRENCY_OPERATION_CODES.filter((item) => allowed.has(item.code));
};

export const currencyOperationCodesToApiString = (codes: string[]): string =>
  codes
    .map((c) => c.trim())
    .filter(Boolean)
    .join(",");

/** Разбор строки API: коды через запятую (обратно к `currencyOperationCodesToApiString`). */
function splitCurrencyOperationCodesString(raw: string): string[] {
  return raw
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
}

export const parseCurrencyOperationCodesFromApi = (raw?: string): string[] => {
  if (!raw?.trim()) {
    return [];
  }
  return splitCurrencyOperationCodesString(raw);
};

const CURRENCY_OPERATION_CODE_TITLE_BY_CODE = new Map(CURRENCY_OPERATION_CODES.map((item) => [item.code, item.title]));

const currencyOperationCodeDisplayLabel = (code: string): string =>
  CURRENCY_OPERATION_CODE_TITLE_BY_CODE.get(code) ?? code;

/** Отображение строки с API: каждый код как наименование из справочника; неизвестные — как введены. */
export const formatCurrencyOperationCodesForDisplay = (raw?: string | null): string => {
  const s = raw != null ? String(raw).trim() : "";
  if (!s) {
    return "";
  }
  const codes = splitCurrencyOperationCodesString(s);
  if (codes.length === 0) {
    return s;
  }
  return codes.map(currencyOperationCodeDisplayLabel).join("; ");
};

export const MAX_CURRENCY_OPERATION_CODE_LENGTH = 5;

export const currencyOperationCodesRequiredRule = () => ({
  validator(_: unknown, value: unknown) {
    if (!Array.isArray(value) || value.length === 0) {
      return Promise.reject(new Error("Выберите или введите код вида операции"));
    }

    const codes = value.map((item) => String(item).trim()).filter(Boolean);
    if (codes.length === 0) {
      return Promise.reject(new Error("Выберите или введите код вида операции"));
    }

    for (const code of codes) {
      if (!/^\d+$/.test(code)) {
        return Promise.reject(new Error("Код вида операции должен содержать только цифры"));
      }
      if (code.length > MAX_CURRENCY_OPERATION_CODE_LENGTH) {
        return Promise.reject(new Error(`Код вида операции — не более ${MAX_CURRENCY_OPERATION_CODE_LENGTH} цифр`));
      }
    }

    return Promise.resolve();
  },
});
